import React from 'react';
import PropTypes from 'prop-types';
import { Deserializer } from 'jsonapi-serializer';
import Raven from 'raven-js';

import withProfile from 'hocs/withProfile';

import ChatMessages from 'api/resources/chatMessages';
import ChatRooms from 'api/resources/chatRooms';
import getErrors from 'utils/getErrors';

import ChatRoomOpenBody from 'chats/ChatRoomOpenBody';

const PAGE_SIZE = 30;

export class ChatRoomOpenBodyContainer extends React.PureComponent {
  state = {
    messagesByHash: {},
    errors: [],
    loading: false,
    next: false,
  }

  componentDidMount() {
    this.reloadChatMessages();
    this.props.socket.addEventListener('message', this.handleMessage);
  }

  componentDidUpdate(prevProps) {
    if (this.props.id !== prevProps.id) {
      this.reloadChatMessages();
    }
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
    this.props.socket.removeEventListener('message', this.handleMessage);
  }

  get lastCreatedAt() {
    return Object.values(this.state.messagesByHash).reduce((min, message) => (
      min > message.createdAt ? message.createdAt : min
    ), Number.MAX_SAFE_INTEGER);
  }

  handleMessage = async (event) => {
    let response;
    try {
      response = JSON.parse(event.data);
    } catch (err) {
      Raven.captureException(err, { extra: this.props });
      return;
    }

    if (!response || !response.data) return;

    let message;
    try {
      message = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);
    } catch (err) {
      Raven.captureException(err, { extra: this.props });
      return;
    }

    if (!message.chatRoom || message.chatRoom.id !== this.props.id) return;

    this.setState(prevState => ({
      messagesByHash: { ...prevState.messagesByHash, [message.id]: message },
    }));

    if (message.chatRoom.hasNewMessages) this.markAsRead(message);
  }

  handleWaypointEnter = () => {
    this.loadChatMessages();
  }

  handleReloadClick = () => {
    this.reloadChatMessages();
  }

  handleMessageSended = (message) => {
    this.setState(prevState => ({
      messagesByHash: { ...prevState.messagesByHash, [message.id]: message },
    }));

    this.props.onMessageSended(message);
  }

  async loadChatMessages() {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({ errors: [], loading: false });

    if (!this.props.id) return;

    this.setState({ loading: true });

    const minCreatedAt = Object.values(this.state.messagesByHash).reduce((min, message) => (
      min > message.createdAt ? message.createdAt : min
    ), Number.MAX_SAFE_INTEGER);

    try {
      const response = await ChatMessages.getAll({
        page: { created_at: minCreatedAt, size: PAGE_SIZE },
        filter: { chat_id: this.props.id },
        include: 'employee,user.avatar,user.oauth_providers,employee.avatar',
        fields: {
          user: 'first_name,last_name,avatar,oauth_providers',
          employee: 'first_name,last_name,avatar',
        },
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const messages = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        messagesByHash: messages.reduce((acc, message) => (
          { ...acc, [message.id]: message }
        ), prevState.messagesByHash),
        loading: false,
        next: messages.length === PAGE_SIZE,
      }));
    } catch (err) {
      if (err.name === 'AbortError') return;

      this.setState({ errors: await getErrors(err), loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  async reloadChatMessages() {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({
      messagesByHash: {},
      errors: [],
      loading: false,
      next: false,
    });

    if (!this.props.id) return;

    this.setState({ loading: true });

    try {
      const response = await ChatMessages.getAll({
        page: { size: PAGE_SIZE },
        filter: { chat_id: this.props.id },
        include: 'employee,user.avatar,user.oauth_providers,employee.avatar',
        fields: {
          user: 'first_name,last_name,avatar,oauth_providers',
          employee: 'first_name,last_name,avatar',
        },
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const messages = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState({
        messagesByHash: messages.reduce((acc, message) => ({ ...acc, [message.id]: message }), {}),
        loading: false,
        next: messages.length === PAGE_SIZE,
      });

      if ((
        this.props.roomType === 'tech_chat' && // в модераторском чате пометка идёт при старте
        this.props.hasNewMessages && messages && messages[0]
      )) {
        this.markAsRead(messages[0]);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;

      this.setState({ errors: await getErrors(err), loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  async markAsRead(message) {
    // комната должна быть залочена на нас
    if ((
      this.props.roomType === 'moderator_chat' &&
      this.props.lockedEmployeeId !== this.props.profile.id
    )) return;

    try {
      const chatRoomResponse = await ChatRooms.moveCursor({
        chatRoomId: this.props.id,
        messageId: message.id,
        include: 'last_message,user,user.avatar,user.oauth_providers',
      });

      const chatRoom = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(chatRoomResponse);

      this.props.onChatRoomRead(chatRoom);
    } catch (err) {
      Raven.captureException(err, { extra: this.props });
      // игнорируем все ошибки
    }
  }

  render() {
    return (
      <ChatRoomOpenBody
        id={this.props.id}

        startChatAutoFocus={this.props.startChatAutoFocus}
        roomType={this.props.roomType}
        locked={this.props.locked}
        lockedEmployeeId={this.props.lockedEmployeeId}
        lockedEmployeeFullname={this.props.lockedEmployeeFullname}
        userId={this.props.userId}
        profileId={this.props.profile.id}

        messagesByHash={this.state.messagesByHash}
        errors={this.state.errors}
        loading={this.state.loading}
        next={this.state.next}

        onWaypointEnter={this.handleWaypointEnter}
        onReloadClick={this.handleReloadClick}
        onMessageSended={this.handleMessageSended}

        onChatRoomStarted={this.props.onChatRoomStarted}
        onChatRoomFinished={this.props.onChatRoomFinished}
      />
    );
  }
}

ChatRoomOpenBodyContainer.propTypes = {
  id: PropTypes.string,

  startChatAutoFocus: PropTypes.bool,
  hasNewMessages: PropTypes.bool,
  roomType: PropTypes.string,
  locked: PropTypes.bool,
  lockedEmployeeId: PropTypes.string,
  lockedEmployeeFullname: PropTypes.string,
  userId: PropTypes.string,

  onChatRoomStarted: PropTypes.func,
  onChatRoomFinished: PropTypes.func,
  onMessageSended: PropTypes.func,
  onChatRoomRead: PropTypes.func,

  socket: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
  }),

  profile: PropTypes.shape({ id: PropTypes.string }),
};

ChatRoomOpenBodyContainer.defaultProps = {
  id: '',

  startChatAutoFocus: false,
  hasNewMessages: false,
  roomType: '',
  locked: false,
  lockedEmployeeId: '',
  lockedEmployeeFullname: '',
  userId: '',

  onChatRoomStarted: () => {},
  onChatRoomFinished: () => {},
  onMessageSended: () => {},
  onChatRoomRead: () => {},

  socket: {
    addEventListener: () => {},
    removeEventListener: () => {},
  },

  profile: {},
};

export default withProfile(ChatRoomOpenBodyContainer);
